import { NguiMapModule } from '@ngui/map';
import { CurrencyPipe, DatePipe, TitleCasePipe, DecimalPipe } from '@angular/common';
import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule, NO_ERRORS_SCHEMA, Injector } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TransferHttpCacheModule } from '@nguniversal/common';
import { CacheModule } from '@ngx-cache/core';
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { Angulartics2Module } from 'angulartics2';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';

import { APP_DI_CONFIG, APP_CONFIG, GOOGLE_MAP_KEY } from './app-config.constants';
import { AppComponent } from './app.component';
import { AppRoutingModule } from './app-routing.module';

import { ErrorComponent } from './components/error/error.component';
import { DrawerModule } from './components/drawer/drawer.module';
import { FooterModule } from './components/footer/footer.module';
import { MinMaxSliderComponent } from './components/forms/min-max-slider/min-max-slider.component';
import { GalleryViewModule } from './components/gallery-view/gallery-view.module';
import { HeaderModule } from './components/header/header.module';
import { HomePageModule } from './components/home-page/home-page.module';
import { LogoModule } from './components/logo/logo.module';
import { NotificationsModule } from './components/notifications/notifications.module';
import { PanelMessageModule } from './components/panel-message/panel-message.module';
import { QuebecModalModule } from './components/quebec-modal/quebec-modal.module';
import { SideNavModule } from './components/side-nav/side-nav.module';

import { GlobalStyleLoaderComponent } from './global-style-loader/global-style-loader.component';

import { MaxPerxSessionGuard } from './guards/maxperx-session.guard';
import { SetRouteCodeGuard } from './guards/set-route-code.guard';

import { MyMissingTranslationHandler } from './handlers/my-missing-translation-handler';

import { AgentProvinceCityResolver } from './resolvers/agent-province-city-resolver';
import { AgentResolver } from './resolvers/agent-resolver';
import { AgentOfficeResolver } from './resolvers/agent-office-resolver'
import { BusinessOpportunitiesResolver } from './resolvers/business-opportunities-resolver';
import { CommercialListingResolver } from './resolvers/commercial-listings-resolver';
import { CommunicationSearchingResolver } from './resolvers/communication-searching-resolver';
import { ListingQueryParamResolver } from './resolvers/listing-query-param-resolver';
import { ListingResolver } from './resolvers/listing-resolver';
import { LuxuryListingResolver } from './resolvers/luxury-listing-resolver';
import { MapCityResolver } from './resolvers/map-city-resolver';
import { OffMarketListingResolver } from './resolvers/off-market-listing-resolver';
import { OfficeResolver } from './resolvers/office-resolver';
import { OpenHouseResolver } from './resolvers/open-house-resolver';
import { LandingPageConfigResolver } from './resolvers/page-config-resolver';
import { PersonaTypeResolver } from './resolvers/persona-type.resolver';
import { PropTypeResolver } from './resolvers/prop-type-resolver';
import { ProvinceResolver } from './resolvers/province-resolver';
import { SavedSearchUnsubscribeResolver } from './resolvers/saved-search-unsubscribe-resolver';
import { ScreenSizeResolver } from './resolvers/screen-size-resolver';
import { UserResolver } from './resolvers/user-resolver';

import { RmTransferHttpCacheInterceptor } from './services/interceptor/rm-transfer-http-cache-interceptor';
import { AgentService } from './services/agent.service';
import { ApiService } from './services/api.service';
import { BlogService } from './services/blog.service';
import { CacheService } from './services/cache.service';
import { CommunicationService } from './services/communication.service';
import { CredentialsService } from './services/credentials.service';
import { DemographicsService } from './services/demographics.service';
import { EnhancedContentService } from './services/enhanced-content.service';
import { GoogleMapsService } from './services/google-maps.service';
import { HomeEstimatorService } from './services/home-estimator.service';
import { ImgixService } from './services/imgix.service';
import { InternalLinksService } from './services/internalLinks.service';
import { LeadsService } from './services/leads.service';
import { ListingsService } from './services/listings.service';
import { LoginService } from './services/login.service';
import { MetaTagService } from './services/meta-tag.service';
import { CanonicalLinkService } from './services/canonical-link.service';
import { MortgageService } from './services/mortgage.service';
import { OfficeService } from './services/office.service';
import { PaginationService } from './services/pagination.service';
import { PostAuthenticationService } from './services/post-authentication.service';
import { PreRenderService } from './services/pre-render.service';
import { PreferencesService } from './services/preferences.service';
import { SaveSearchService } from './services/save-search.service';
import { SocialService } from './services/social.service';
import { WebfontLoaderService } from './services/webfont-loader.service';

import { SharedDirectivesModule } from './shared/directives/shared-directives.module';
import { SharedPipesModule } from './shared/pipes/shared-pipes.module';
import { SharedModule } from './shared/shared.module';

import { environment } from '../environments/environment-wrapper';
import { AdPostalCodeService } from './services/ad-postal-code.service';
import { PointsOfInterestService } from './services/points-of-interest.service';
import { ProxyRouteComponent } from './components/login/proxy-route.component';
import { UrlSanitizerService } from './services/url-sanitizer.service';
import { RedirectGuard } from './guards/redirect.guard';

// AoT requires an exported function for factories
export function HttpLoaderFactory(injector: Injector) {
  const http = injector.get(HttpClient);
  return new TranslateHttpLoader(http, environment.staticResourceRoot + 'assets/i18n/', '.ng_app_version.json');
}

@NgModule({
  declarations: [
    AppComponent,
    MinMaxSliderComponent,      // Used in a classes/helper.ts
    GlobalStyleLoaderComponent,
    ErrorComponent,
    ProxyRouteComponent, // Used to enable lazyloading of modules with outlet routes
    // Components for HomePageComponent

  ],
  imports: [
    BrowserModule.withServerTransition({ appId: 'remaxdotca' }),
    Angulartics2Module.forRoot([Angulartics2GoogleTagManager]),
    SharedModule,
    SharedPipesModule.forRoot(),
    SharedDirectivesModule,
    NguiMapModule.forRoot({apiUrl: `https://maps.google.com/maps/api/js?key=${GOOGLE_MAP_KEY}&libraries=places`}),
    BrowserAnimationsModule,
    AppRoutingModule,
    HomePageModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [Injector],
      },
      missingTranslationHandler: {
        provide: MissingTranslationHandler, useClass: MyMissingTranslationHandler
      }
    }),
    TransferHttpCacheModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    CacheModule.forRoot(),
    // Modules for HomePageComponent
    GalleryViewModule,
    LogoModule,
    NotificationsModule,
    PanelMessageModule,
    // Modules for the root layout.
    SideNavModule,
    HeaderModule,
    FooterModule,
    DrawerModule,
    QuebecModalModule
  ],
  exports: [
    MinMaxSliderComponent
  ],
  providers: [
    ImgixService,
    PaginationService,
    PreferencesService,
    CacheService,
    DemographicsService,
    ListingsService,
    PointsOfInterestService,
    LeadsService,
    CommunicationService,
    BlogService,
    AgentService,
    ApiService,
    CredentialsService,
    LoginService,
    EnhancedContentService,
    GoogleMapsService,
    MortgageService,
    SocialService,
    OfficeService,
    InternalLinksService,
    CurrencyPipe,
    DecimalPipe,
    TitleCasePipe,
    PreRenderService,
    UrlSanitizerService,
    MetaTagService,
    CanonicalLinkService,
    AdPostalCodeService,
    RmTransferHttpCacheInterceptor,
    HomeEstimatorService,
    PostAuthenticationService,
    SaveSearchService,
    WebfontLoaderService,
    DatePipe,
    SetRouteCodeGuard,
    RedirectGuard,
    MaxPerxSessionGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useExisting: RmTransferHttpCacheInterceptor,
      multi: true
    },
    // Resolvers
    OfficeResolver, UserResolver, ScreenSizeResolver, ListingResolver, AgentResolver, AgentOfficeResolver, MapCityResolver, CommunicationSearchingResolver, PropTypeResolver,
    AgentProvinceCityResolver, CommercialListingResolver, ProvinceResolver, LandingPageConfigResolver, LuxuryListingResolver, OpenHouseResolver, PersonaTypeResolver,
    ListingQueryParamResolver, SavedSearchUnsubscribeResolver, OffMarketListingResolver, BusinessOpportunitiesResolver,
    {
      provide: APP_CONFIG,
      useValue: APP_DI_CONFIG
    }
  ],
  bootstrap: [AppComponent],
  schemas: [NO_ERRORS_SCHEMA] // we include this so that we can use multi select ng contents for components
})
export class AppModule { }
